/**
 * Lógica pura de los skeletons de tabla · normaliza la definición de columnas
 * (la MISMA que usa la tabla real: pesos relativos `w` o porcentajes `pct`) y
 * reparte anchos de barra deterministas para que las filas no se vean clonadas.
 */

export type AlineacionColumna = "left" | "right" | "center";

export interface ColumnaSkeleton {
  /** Peso relativo (se normaliza a % junto con el resto de columnas con `w`). */
  w?: number;
  /** Ancho explícito (p. ej. "12.5%") · gana sobre `w`. */
  pct?: string;
  align?: AlineacionColumna;
  /** Líneas de texto de la celda real (dato + subdato) · default 1. */
  lineas?: 1 | 2;
}

export interface ColumnaNormalizada {
  pct: string | undefined;
  align: AlineacionColumna;
  lineas: 1 | 2;
}

/**
 * Normaliza columnas → ancho en % (si hay pesos) + alineación. Un número N crea
 * N columnas iguales. Las columnas con `pct` lo conservan; las de `w` se reparten
 * en proporción a su peso (mismo cálculo que las tablas `layout: fixed` del portal).
 */
export function normalizarColumnas(columnas: ReadonlyArray<ColumnaSkeleton> | number): ColumnaNormalizada[] {
  const lista: ReadonlyArray<ColumnaSkeleton> =
    typeof columnas === "number" ? Array.from({ length: Math.max(1, Math.floor(columnas)) }, () => ({ w: 1 })) : columnas;
  const totalPeso = lista.reduce((acc, c) => acc + (c.pct ? 0 : (c.w ?? 0)), 0);
  return lista.map((c) => ({
    pct: c.pct ?? (totalPeso > 0 && c.w ? `${((c.w / totalPeso) * 100).toFixed(2)}%` : undefined),
    align: c.align ?? "left",
    lineas: c.lineas ?? 1,
  }));
}

const ANCHOS = ["80%", "62%", "90%", "70%", "55%", "85%"] as const;

/** Ancho de la barra de la celda (fila, columna) · cíclico y determinista. */
export function anchoBarra(fila: number, columna: number): string {
  return ANCHOS[(fila * 7 + columna * 3) % ANCHOS.length];
}
